import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

interface CountLabelPairDraft {
  one?: string;
  other?: string;
}
interface CountLabelsDraft {
  catalog?: CountLabelPairDraft;
  pack?: CountLabelPairDraft;
}

function pairOrEmpty(v: CountLabelPairDraft | undefined): CountLabelPairDraft {
  return v ?? {};
}

/**
 * `kind: "countLabels"` — the overridable "N catalogs"/"N packs" noun pair
 * (`Catalog.countLabels`, docs/260-OVERRIDABLE-COUNT-LABEL-NOUNS.md). Each of the
 * 4 leaf strings is independently optional; an all-blank pair collapses to
 * `undefined`, and an all-blank `countLabels` collapses the whole field.
 */
export function CountLabelsEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const draft: CountLabelsDraft = value && typeof value === "object" ? (value as CountLabelsDraft) : {};

  const setLeaf = (group: "catalog" | "pack", leaf: "one" | "other", v: string) => {
    const pair = { ...pairOrEmpty(draft[group]), [leaf]: v || undefined };
    const cleanedPair = pair.one || pair.other ? pair : undefined;
    const next: CountLabelsDraft = { ...draft, [group]: cleanedPair };
    if (!next.catalog) delete next.catalog;
    if (!next.pack) delete next.pack;
    onChange(Object.keys(next).length > 0 ? next : undefined);
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      {(["catalog", "pack"] as const).map((group) => (
        <Stack key={group} direction="row" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ minWidth: 60, alignSelf: "center" }} color="text.secondary">
            {group}
          </Typography>
          <TextField
            size="small"
            label="one (e.g. '{n} course')"
            value={pairOrEmpty(draft[group]).one ?? ""}
            onChange={(e) => setLeaf(group, "one", e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <TextField
            size="small"
            label="other (e.g. '{n} courses')"
            value={pairOrEmpty(draft[group]).other ?? ""}
            onChange={(e) => setLeaf(group, "other", e.target.value)}
            sx={{ minWidth: 180 }}
          />
        </Stack>
      ))}
    </Box>
  );
}
